import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CalendarCheck, Zap } from 'lucide-react';
import { closedReason, dueOf, dueRank, toneOf } from '../../../lib/eventJourney';
import { formatLocalDate } from '../../../lib/eventWorkspace';
import GuestCard from './GuestCard';
import { AutoBolt, TonePill, shortDate } from './guestUi';

/** What an empty column says, by phase: it opens later, it is done, it fills by itself, or you drop into it. */
export function emptyText(column, phase) {
    if (phase === 'before' && column.phase === 'after') return 'Opens when the event starts';
    if (phase === 'after' && column.phase === 'before' && column.kind === 'milestone') return 'Empty once the event has ended';
    return column.auto ? 'Cards arrive here on their own' : 'Drop a card here';
}

/**
 * The journey board (#1013): one row of fixed-width columns, left to right, from To invite to Closed. No
 * column ever collapses; the event day is a labelled line between Going and Attended, not a split. Every
 * column shows its guidance in every phase. Dropping a card asks for the move (the caller opens the form).
 */
export default function GuestsBoard({
    columns, cards, columnOf, phase, event, selectedIds, spotlight, flashIds, readOnly, scrollTarget,
    onOpen, onToggle, onAction, onRequestMove,
}) {
    const boardRef = useRef(null);
    const [dragId, setDragId] = useState(null);
    const [overKey, setOverKey] = useState(null);

    const byColumn = useMemo(() => {
        const groups = Object.fromEntries(columns.map((column) => [column.key, []]));
        cards.forEach((card) => { (groups[columnOf(card)] || (groups[columnOf(card)] = [])).push(card); });
        return groups;
    }, [columns, cards, columnOf]);

    // Open (or re-open) the board where the work is: a metric click, or the phase on first load.
    useEffect(() => {
        const board = boardRef.current;
        if (!board || !scrollTarget) return;
        const target = scrollTarget.key ? board.querySelector(`[data-col="${scrollTarget.key}"]`) : null;
        // Measured from the board's own left edge: `offsetLeft` is relative to the nearest positioned ancestor, which
        // is not the board, so it overshot by the board's distance from that ancestor.
        board.scrollLeft = target ? Math.max(0, board.scrollLeft + target.getBoundingClientRect().left - board.getBoundingClientRect().left - 4) : 0;
    }, [scrollTarget]);

    const drop = (event, key) => {
        event.preventDefault();
        const id = (event.dataTransfer && event.dataTransfer.getData && event.dataTransfer.getData('text/plain')) || dragId;
        setDragId(null);
        setOverKey(null);
        if (!id || readOnly) return;
        const ids = selectedIds.includes(id) ? selectedIds : [id];
        const moving = cards.filter((card) => ids.includes(card.contact_id));
        if (moving.length) onRequestMove(moving, key);
    };

    return (
        <div className="space-y-2" data-testid="guests-board-wrap">
            <p data-testid="guests-board-hint" className="flex items-center gap-1.5 text-xs text-stone-500">
                <span className="inline-flex h-4 w-4 items-center justify-center rounded bg-amber-100 text-amber-700"><Zap className="h-3 w-3" aria-hidden="true" /></span>
                Moves on its own when an invite is sent, an RSVP comes in, someone checks in or the event ends. Drop a card in by hand and you&apos;ll be asked to record it.
            </p>
            <div ref={boardRef} data-guest-board="1" data-testid="guests-board" className="overflow-x-auto pb-2" style={{ scrollBehavior: 'smooth' }}>
                <div className="flex w-max items-stretch gap-2.5">
                    {columns.map((column) => {
                        const list = (byColumn[column.key] || []).map((card) => ({ card, due: dueOf(card, column.key, phase, shortDate) }))
                            .sort((a, b) => dueRank(a.due) - dueRank(b.due) || a.card.name.localeCompare(b.card.name));
                        const closed = column.key === 'closed';
                        const reasons = closed ? Object.entries(list.reduce((memo, { card }) => { const r = closedReason(card); memo[r] = (memo[r] || 0) + 1; return memo; }, {})) : [];
                        const dim = spotlight && spotlight !== column.key;
                        const over = dragId && overKey === column.key;
                        const t = toneOf(column.key, columns);
                        const notYet = phase === 'before' && column.phase === 'after';
                        const muted = notYet || (phase === 'after' && column.phase === 'before' && column.kind === 'milestone' && list.length === 0);
                        return (
                            <React.Fragment key={column.key}>
                                {column.key === 'attended' && (
                                    <div data-col="eventday" data-testid="guests-event-day" className="flex w-[84px] shrink-0 flex-col items-center gap-1.5 pt-2.5" title="Columns to the right fill at or after the event">
                                        <span className={`inline-flex h-6 items-center gap-1 whitespace-nowrap rounded-full px-2.5 text-[11.5px] font-semibold ${phase === 'event_day' ? 'bg-stone-900 text-white' : 'border border-stone-200 bg-white text-stone-700'}`}>
                                            <CalendarCheck className="h-3 w-3" aria-hidden="true" />{phase === 'event_day' ? 'Live now' : 'Event day'}
                                        </span>
                                        <span className="text-center text-[11px] text-stone-500">{event?.date ? `${phase === 'after' ? 'Held ' : ''}${formatLocalDate(event.date)}` : ''}</span>
                                        <span className={`min-h-[200px] w-0 flex-1 border-l-2 ${phase === 'event_day' ? 'border-solid border-stone-900' : 'border-dashed border-stone-300'}`} />
                                    </div>
                                )}
                                <section
                                    data-col={column.key}
                                    data-testid={`guests-column-${column.key}`}
                                    aria-label={`${column.label} column`}
                                    onDragOver={readOnly ? undefined : (event) => { event.preventDefault(); if (overKey !== column.key) setOverKey(column.key); }}
                                    onDragLeave={readOnly ? undefined : () => setOverKey((current) => (current === column.key ? null : current))}
                                    onDrop={readOnly ? undefined : (event) => drop(event, column.key)}
                                    className={`flex w-[252px] shrink-0 flex-col gap-2.5 rounded-[10px] border p-2.5 ${spotlight === column.key ? 'border-stone-900' : over ? 'border-dashed border-stone-500' : 'border-stone-200'}`}
                                    style={{ flex: '0 0 252px', background: over ? '#EFEDE8' : muted || closed ? '#FBFAF9' : '#F6F5F1', opacity: dim ? 0.42 : 1 }}
                                >
                                    <header className="flex items-center gap-2">
                                        <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: notYet ? '#D6D3CD' : t.dot }} />
                                        <h3 className={`min-w-0 truncate text-[13px] font-semibold ${muted ? 'text-stone-500' : 'text-stone-900'}`}>{column.label}</h3>
                                        {column.auto && <AutoBolt title={column.tip || column.description} />}
                                        <span className="flex-1" />
                                        <span data-testid={`guests-column-count-${column.key}`} className="inline-flex h-5 min-w-[22px] items-center justify-center rounded-full border border-stone-200 bg-white px-1.5 text-[11.5px] font-semibold text-stone-600">{list.length}</span>
                                    </header>
                                    <p data-testid={`guests-column-desc-${column.key}`} className="text-xs leading-snug text-stone-500">{column.description}</p>
                                    {reasons.length > 0 && <div className="flex flex-wrap gap-1">{reasons.map(([reason, count]) => <TonePill key={reason}>{`${reason} ${count}`}</TonePill>)}</div>}
                                    <div className="flex flex-col gap-2">
                                        {list.map(({ card, due }) => (
                                            <GuestCard key={card.contact_id} card={card} columnKey={column.key} columns={columns} phase={phase} due={due}
                                                selected={selectedIds.includes(card.contact_id)} flash={flashIds.includes(card.contact_id)} dragging={dragId === card.contact_id}
                                                readOnly={readOnly} onOpen={onOpen} onToggle={onToggle} onAction={onAction}
                                                onDragStart={(dragged) => setDragId(dragged.contact_id)} onDragEnd={() => { setDragId(null); setOverKey(null); }} />
                                        ))}
                                        {list.length === 0 && (
                                            <div data-testid={`guests-column-empty-${column.key}`} className="rounded-lg border border-dashed border-stone-300 px-3 py-5 text-center text-xs text-stone-400">{emptyText(column, phase)}</div>
                                        )}
                                    </div>
                                </section>
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
